/**
 * TopUpUsdt — экран пополнения USDT: QR-код и адрес кастодиального Tron-кошелька клиента.
 * Депозит крипты на СВОЙ кошелёк — без KYC. Пропсы: address, onBack.
 */
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Screen, Card, FallbackButton } from "@/components/Screen";
import { Spinner } from "@/components/Spinner";
import { useBackButton } from "@/telegram/useMainButton";
import { useCopy, CopyIcon } from "@/components/copy";

type Props = { address: string; onBack: () => void };

export function TopUpUsdt({ address, onBack }: Props) {
  useBackButton(onBack);
  const [qr, setQr] = useState<string | null>(null);
  const { copied, copy } = useCopy(address);
  const net = "TRC20"; // стандарт сети USDT на Tron (а не chain-env mainnet/nile)

  useEffect(() => {
    if (!address) return;
    let alive = true;
    QRCode.toDataURL(address, { width: 260, margin: 1, color: { dark: "#0C1013", light: "#ffffff" } })
      .then((url) => alive && setQr(url))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [address]);

  return (
    <Screen title="Пополнить USDT" subtitle={`Отправьте USDT в сети ${net} на этот адрес — средства зачислятся автоматически.`}>
      <div className="space-y-4">
        {/* QR */}
        <div className="flex justify-center">
          <div className="rounded-3xl bg-white p-4 shadow-2xl shadow-black/50">
            {address && qr ? (
              <img src={qr} alt="QR" width={220} height={220} className="h-[220px] w-[220px] rounded-lg" />
            ) : (
              <div className="grid h-[220px] w-[220px] place-items-center">
                <Spinner className="h-8 w-8" />
              </div>
            )}
          </div>
        </div>

        {/* Адрес + копирование */}
        <Card className="!p-3">
          <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-white/40">Адрес кошелька · {net}</div>
          <div className="mt-1.5 break-all font-mono text-[14px] leading-snug text-white/90">{address || "—"}</div>
          <button
            onClick={copy}
            disabled={!address}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-[15px] font-semibold text-surface-base active:brightness-95 disabled:opacity-50"
          >
            <CopyIcon className="h-4 w-4" />
            {copied ? "Скопировано" : "Скопировать адрес"}
          </button>
        </Card>

        {/* Предупреждение о сети */}
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.07] p-3.5 text-[13px] leading-snug text-amber-200/90">
          Отправляйте только <span className="font-semibold">USDT в сети {net}</span>. Перевод других токенов или
          из другой сети приведёт к потере средств.
        </div>
      </div>
      <FallbackButton text="Назад" onClick={onBack} />
    </Screen>
  );
}
